import { t } from '@transifex/native';
import { memo } from 'react';

import DateTime from 'pkg/datetime';

export const formatRelativeDateTimeString = (
	dateTime: number,
	hideTime = false
) => {
	const dt = new DateTime(new Date(dateTime * 1000));
	const now = new DateTime(new Date());

	const unixTime = dateTime;
	const nowUnixTime = Math.round(Date.now() / 1000);

	const nowDate = now.toDate();
	const dtDate = dt.toDate();

	const sameYear = () => nowDate.getFullYear() === dtDate.getFullYear();

	const sameWeek = () => nowUnixTime - unixTime < 60 * 60 * 24 * 7;

	const sameMonth = () =>
		sameYear() && nowDate.getMonth() === dtDate.getMonth();

	const sameDay = () =>
		sameYear() && sameMonth() && nowDate.getDate() === dtDate.getDate();

	const timeOptions: {
		hour?: 'numeric';
		minute?: 'numeric';
	} = hideTime
		? {}
		: {
				hour: 'numeric',
				minute: 'numeric',
			};

	//Future
	if (unixTime > nowUnixTime) {
		return dt.toLocaleDateString({
			day: 'numeric',
			month: 'short',
			year: 'numeric',
			...timeOptions,
		});
	}

	// Just now
	if (nowUnixTime - unixTime < 60 * 3) {
		return t('Just now');
	}

	// Today
	if (sameDay()) {
		return t('Today {value}', {
			value: dt.toTimeString(),
		});
	}

	// Same week
	if (sameWeek()) {
		return dt.toLocaleDateString({
			weekday: 'long',
			...timeOptions,
		});
	}

	// Same year
	if (sameYear()) {
		return dt.toLocaleDateString({
			weekday: 'short',
			day: 'numeric',
			month: 'short',
			...timeOptions,
		});
	}

	// Another year
	return dt.toLocaleDateString({
		day: 'numeric',
		month: 'short',
		year: 'numeric',
		...timeOptions,
	});
};

const timeAgoUnits: [Intl.RelativeTimeFormatUnit, number][] = [
	['year', 60 * 60 * 24 * 365],
	['month', 60 * 60 * 24 * 30],
	['day', 60 * 60 * 24],
	['hour', 60 * 60],
	['minute', 60],
];

export const formatTimeAgoString = (
	dateTime: number,
	now: number = Math.round(Date.now() / 1000)
) => {
	const elapsed = now - dateTime;
	const match = timeAgoUnits.find(([, seconds]) => elapsed >= seconds);

	if (!match) {
		return t('Just now');
	}

	const [unit, seconds] = match;

	return new Intl.RelativeTimeFormat(DateTime.getLocale(), {
		numeric: 'always',
	}).format(-Math.floor(elapsed / seconds), unit);
};

interface RelativeDateTimeProps {
	dateTime: number;
	hideTime?: boolean;
	timeAgo?: boolean;
	className?: string;
	prefix?: string;
	suffix?: string;
}

const RelativeDateTime = memo(
	({
		dateTime,
		hideTime = false,
		timeAgo = false,
		className,
		prefix,
		suffix,
	}: RelativeDateTimeProps) => {
		const dt = DateTime.fromTimestamp(dateTime);
		const formattedDateTime = dt.toLocaleDateString({
			day: 'numeric',
			month: 'short',
			year: 'numeric',
			...(hideTime
				? {}
				: {
						hour: 'numeric',
						minute: 'numeric',
					}),
		});

		return (
			<time
				className={className}
				dateTime={formattedDateTime}
				title={formattedDateTime}>
				{prefix}{' '}
				{timeAgo
					? formatTimeAgoString(dateTime)
					: formatRelativeDateTimeString(dateTime, hideTime)}{' '}
				{suffix}
			</time>
		);
	}
);

export default RelativeDateTime;
